import { useCallback, useEffect, useRef, useState } from 'react';
import { registerFlusher, useSaves } from '../state/saves';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

type Entity = { revision: number } & Record<string, unknown>;

/**
 * 입력 즉시 저장 (계획서 §17-1).
 * - 입력 후 ~0.6초에 저장. 탭 닫기·백그라운드 전환·화면 이탈·자동잠금 시 즉시 저장(flush).
 * - 저장할 때 마지막으로 알고 있는 revision을 함께 보낸다 → 다른 탭/기기가 먼저 바꿨다면
 *   덮어쓰지 않고 'conflict'로 멈춘다. 입력한 내용은 화면에 그대로 남아 있다.
 */
export function useAutosave<T extends Entity>(
  initial: T | null,
  save: (patch: Partial<T>, expectedRevision: number) => Promise<T>,
  opts: { delayMs?: number; readOnly?: boolean } = {},
) {
  const delay = opts.delayMs ?? 600;
  const [entity, setEntity] = useState<T | null>(initial);
  const [local, setLocal] = useState<Partial<T>>({});
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const entityRef = useRef(entity);
  const pendingRef = useRef<Partial<T>>({});
  const timer = useRef<number | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const saveRef = useRef(save);
  const { inc, dec } = useSaves.getState();
  const counted = useRef(false);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  // 초기값이 바뀌면 (다른 entity를 열었을 때) 상태 초기화
  const initialId = (initial as { id?: string } | null)?.id;
  const [prevId, setPrevId] = useState(initialId);
  if (prevId !== initialId) {
    setPrevId(initialId);
    setEntity(initial);
    setLocal({});
    setStatus('idle');
  }
  useEffect(() => {
    entityRef.current = entity;
  }, [entity]);

  const markCounted = (on: boolean) => {
    if (on && !counted.current) {
      counted.current = true;
      inc();
    } else if (!on && counted.current) {
      counted.current = false;
      dec();
    }
  };

  const flush = useCallback(async (): Promise<void> => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (inFlight.current) await inFlight.current;
    const patch = pendingRef.current;
    const base = entityRef.current;
    if (!base || Object.keys(patch).length === 0) {
      markCounted(false);
      return;
    }
    pendingRef.current = {};
    setStatus('saving');
    const run = (async () => {
      try {
        const saved = await saveRef.current(patch, base.revision);
        entityRef.current = saved;
        setEntity(saved);
        // 저장 중에 새로 입력된 값은 남겨둔다
        setLocal((cur) => {
          const next = { ...cur };
          for (const k of Object.keys(patch)) if (!(k in pendingRef.current)) delete next[k];
          return next;
        });
        setStatus(Object.keys(pendingRef.current).length ? 'pending' : 'saved');
        setError(null);
      } catch (e) {
        pendingRef.current = { ...patch, ...pendingRef.current };
        const name = (e as Error)?.name;
        setStatus(name === 'RevisionConflictError' ? 'conflict' : 'error');
        setError((e as Error)?.message ?? '저장 실패');
        return;
      }
      if (Object.keys(pendingRef.current).length === 0) markCounted(false);
    })();
    inFlight.current = run;
    await run;
    inFlight.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      if (opts.readOnly) return;
      setLocal((cur) => ({ ...cur, [key]: value }));
      pendingRef.current = { ...pendingRef.current, [key]: value };
      markCounted(true);
      setStatus((s) => (s === 'conflict' ? s : 'pending'));
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay, flush, opts.readOnly],
  );

  // 탭 닫기·백그라운드 전환·화면 이탈 시 즉시 저장
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    const onPageHide = () => void flush();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    const unregister = registerFlusher(async () => {
      await flush();
    });
    return () => {
      unregister();
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      void flush();
    };
  }, [flush]);

  /** 충돌 해결: 최신 내용을 불러와 그 위에 내 입력을 새 revision으로 저장 (사용자가 명시적으로 선택) */
  const resolveWithMine = useCallback(
    async (latest: T) => {
      entityRef.current = latest;
      setEntity(latest);
      setStatus('pending');
      await flush();
    },
    [flush],
  );

  /** 충돌 해결: 다른 곳의 최신 내용을 받아들이고 내 미저장 입력은 버린다 */
  const resolveWithTheirs = useCallback((latest: T) => {
    pendingRef.current = {};
    entityRef.current = latest;
    setEntity(latest);
    setLocal({});
    setStatus('saved');
    markCounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = entity ? ({ ...entity, ...local } as T) : null;
  return { value, entity, set, flush, status, error, resolveWithMine, resolveWithTheirs, setEntity };
}
